import get from 'lodash/get'
import set from 'lodash/set'
import { ReduxState } from 'app-types'
import * as Sentry from '@sentry/browser'
import { AxiosResponse } from 'axios'

import { LOG_LEVELS } from '../constants/errors'
import endpoints, { Endpoint } from '../constants/endpoints'
import { ComponentError, ResponseError } from './errors'
import { ProcessStatus } from '../types'
import { ConfigData } from '../sagas/app/config/config.mapper'

const ajaxQuery = new XMLHttpRequest()

export interface MapCreateLogPayload {
  error: ResponseError | ComponentError
  status?: ProcessStatus
  level?: string
}

const mapCreateLog = (payload: MapCreateLogPayload) => {
  const appId = window.APP_ID
  const environment = window.ENVIRONMENT

  const error = get(payload, 'error')
  const status = get(payload, 'status')
  const level = get(payload, 'level')
  const relatedData = get(payload, 'error.relatedData')
  if (status) {
    relatedData.processStatus = status
  }

  let name = JSON.stringify(error.name || '')

  if (error.type) {
    name = `${name} - ${JSON.stringify(error.type)}`
  }

  return {
    appId,
    level: level || LOG_LEVELS.ERROR,
    name,
    message: error.message ? JSON.stringify(error.message) : '',
    stacktrace: JSON.stringify(error.stackTrace),
    clientCreatedAt: new Date(),
    environment,
    relatedData: JSON.stringify(relatedData),
  }
}

const createLog = (payload: MapCreateLogPayload) => {
  try {
    const endpoint: Endpoint = endpoints.openApi.createLog
    const mappedPayload = mapCreateLog(payload)
    ajaxQuery.open(endpoint.method.toUpperCase(), `${window.API_URL}${endpoint.url}`)
    ajaxQuery.setRequestHeader('X-Flow-Id', get(payload, 'status.xflowId'))
    ajaxQuery.setRequestHeader('Content-Type', 'application/json')
    ajaxQuery.send(JSON.stringify(mappedPayload))
  } catch (ex) {
    window.console.log(ex)
  }
}

const logSagaError = (sagaName: string, status: ProcessStatus, error: ResponseError) => {
  createLog({ error, status, level: LOG_LEVELS.ERROR })

  if (window.IS_SENTRY) {
    const response: AxiosResponse = get(error, 'response')
    if (response) {
      set(response, 'config.data', 'REMOVED')
      Sentry.setExtra('response', response)
    }
    if (error.stack) {
      Sentry.setExtra('error.stack', get(error, 'stack'))
    }
    if (error.sagaStack) {
      Sentry.setExtra('error.sagaStack', get(error, 'sagaStack'))
    }

    Sentry.captureException(new Error(`Saga error, name= ${sagaName}`))
  }
}

const logSagaCall = (sagaName: string, status: ProcessStatus) => {
  if (window.IS_SENTRY) {
    Sentry.addBreadcrumb({
      category: 'saga call',
      message: sagaName,
      level: Sentry.Severity.Info,
    })
    Sentry.setExtra('uploaderProcessStatus', status)
    Sentry.setExtra('error.stack', null)
    Sentry.setExtra('error.sagaStack', null)
    Sentry.setExtra('error.stackTrace', null)
    Sentry.setExtra('response', null)
  }
}

const selectProcessStatus = (state: ReduxState): ProcessStatus => ({
  xflowId: get(state, 'app.flowId'),
})

const initFromAppConfig = (config: ConfigData, xflowId: string) => {
  if (config.sentry) {
    Sentry.init({
      dsn: config.sentry,
      environment: config.environment,
    })

    setFlowId(xflowId)

    Sentry.setTag('release_version', window.RELEASE_VERSION)
    Sentry.setTag('release_timestamp', window.RELEASE_TIMESTAMP)
  }
  window.IS_SENTRY = !!config.sentry
}

const setFlowId = (xflowId: string) => {
  Sentry.setUser({ xflowId })
  Sentry.setTag('flow_id', xflowId)
}

export const eventLogger = {
  createLog,
  initFromAppConfig,
  logSagaCall,
  logSagaError,
  setFlowId,
  selectProcessStatus,
}
